import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useOrgMembers } from '@/hooks/useTeamManagement'
import {
  type OrganizationInvitation,
  USER_INVITE_TYPE,
  UNIFIED_TEAM_INVITATIONS_KEYS,
} from '@/hooks/useOrganizationInvitations'
import type { ProductRole, UserRole } from '@dcp/shared'

/**
 * A unified team member representing an active org member (kind='member')
 * or a pending/processing/expired invitation (kind='invitation').
 *
 * Discriminated union lets callers narrow with kind checks and get
 * type-safe access to status-specific fields (e.g. expiresAt, resendCount).
 */
export type UnifiedTeamMember =
  | {
      kind: 'member'
      id: string
      fullName: string | null
      email: string
      role: UserRole | string
      productRole: ProductRole | null
      createdAt: string
      status: 'active'
    }
  | {
      kind: 'invitation'
      id: string
      firstName: string | null
      lastName: string | null
      email: string
      productRole: ProductRole | null
      createdAt: string
      expiresAt: string
      status: 'pending' | 'processing' | 'expired'
      resendCount: number
    }

/**
 * Fetch open + processing + expired user-type invitations for the current org.
 *
 * The existing useOrgInvitations only fetches status='open'. We need a broader
 * set for the unified list (AC9), so this hook calls the same edge function
 * without a status filter and then filters client-side to exclude 'accepted'
 * and 'cancelled' rows (which are either already member rows or irrelevant).
 */
function useOrgInvitationsAll() {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.invitations(orgId),
    queryFn: async () => {
      if (!orgId) return []

      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required')
      }

      // Fetch all invitations without status filter so we capture open,
      // processing, and expired rows in a single round-trip.
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: 'list' }),
        }
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error((error as { error?: string }).error || 'Failed to fetch invitations')
      }

      const result = await response.json()

      // Keep only user-type invitations for this org, excluding terminal states.
      return ((result.data || []) as OrganizationInvitation[]).filter(
        (inv) =>
          inv.invite_type === USER_INVITE_TYPE &&
          inv.target_organization_id === orgId &&
          inv.status !== 'accepted' &&
          inv.status !== 'cancelled'
      )
    },
    enabled: !!orgId,
  })
}

/**
 * Map an OrganizationInvitation status to the UnifiedTeamMember invitation status.
 * 'open' maps to 'pending'; everything else keeps its value as-is.
 * @param status - Raw DB status from OrganizationInvitation
 * @returns Narrowed status for the unified member union
 */
function toInvitationStatus(
  status: OrganizationInvitation['status']
): 'pending' | 'processing' | 'expired' {
  if (status === 'open') return 'pending'
  if (status === 'processing') return 'processing'
  return 'expired'
}

/**
 * Hook that returns a stable, sorted unified list of all team members and
 * open/processing/expired invitations for the current organization.
 *
 * Sort order:
 *   1. Invitations first (pending → processing → expired), newest first within group
 *   2. Active members last, newest first
 *
 * Returns isLoading=true until both underlying queries have completed their
 * first fetch so the table never flickers between partial states.
 *
 * @returns { data, isLoading, isError } — data is UnifiedTeamMember[]
 */
export function useTeamMembers() {
  const membersQuery = useOrgMembers()
  const invitationsQuery = useOrgInvitationsAll()

  const data = useMemo<UnifiedTeamMember[]>(() => {
    const members = membersQuery.data ?? []
    const invitations = invitationsQuery.data ?? []

    const STATUS_ORDER: Record<'pending' | 'processing' | 'expired', number> = {
      pending: 0,
      processing: 1,
      expired: 2,
    }

    // Build invitation rows, oldest-created last (newest first within each status group)
    const invitationRows: UnifiedTeamMember[] = invitations
      .map((inv): UnifiedTeamMember => ({
        kind: 'invitation',
        id: inv.id,
        firstName: inv.admin_first_name,
        lastName: inv.admin_last_name,
        email: inv.admin_email,
        // `?? null` is load-bearing: an invite row that omits product_role
        // reads as undefined at runtime; normalize to null.
        productRole: (inv.product_role as ProductRole | null) ?? null,
        createdAt: inv.created_at,
        expiresAt: inv.expires_at,
        status: toInvitationStatus(inv.status),
        resendCount: inv.resend_count,
      }))
      .sort((a, b) => {
        if (a.kind !== 'invitation' || b.kind !== 'invitation') return 0
        const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
        if (statusDiff !== 0) return statusDiff
        // Within same status: newest first
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })

    // Build active member rows, newest first
    const memberRows: UnifiedTeamMember[] = [...members]
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      .map((m): UnifiedTeamMember => ({
        kind: 'member',
        id: m.id,
        fullName: m.full_name,
        email: m.email,
        role: m.role,
        // `?? null` mirrors the invitation branch: normalize a possibly-undefined
        // product_role to null so undefined never leaks into the ProductRole | null union.
        productRole: m.product_role ?? null,
        createdAt: m.created_at,
        status: 'active',
      }))

    return [...invitationRows, ...memberRows]
  }, [membersQuery.data, invitationsQuery.data])

  return {
    data,
    isLoading: membersQuery.isLoading || invitationsQuery.isLoading,
    isError: membersQuery.isError || invitationsQuery.isError,
  }
}
